import { initializeApp } from "firebase/app";
import { getMessaging, getToken, isSupported as isMessagingSupported } from "firebase/messaging";
import FingerprintJS from "@fingerprintjs/fingerprintjs";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FB_API_KEY,
  authDomain: process.env.REACT_APP_FB_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FB_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FB_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FB_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FB_APP_ID,
};

// Initialize Firebase app
const app = initializeApp(firebaseConfig);

// Lazy initialization of Firebase Messaging
let messagingInstance = null;
let messagingPromise = null;

// Helper function to detect if running in WebView
function isWebView() {
  const ua = navigator.userAgent || navigator.vendor || window.opera;

  // Check for common WebView indicators
  const isAndroidWebView = /wv/.test(ua) || /Android.*AppleWebKit(?!.*Chrome)/.test(ua);
  const isIOSWebView = /(iPhone|iPod|iPad).*AppleWebKit(?!.*Safari)/.test(ua);

  return isAndroidWebView || isIOSWebView;
}

export async function initializeMessaging() {
  if (messagingInstance) {
    return messagingInstance;
  }

  if (messagingPromise) {
    return messagingPromise;
  }

  messagingPromise = (async () => {
    try {
      // Don't initialize Firebase Messaging in WebViews
      if (isWebView()) {
        console.log("Running in WebView - skipping Firebase Messaging initialization");
        return null;
      }

      const isSupported = await isMessagingSupported();
      console.log("Firebase Messaging isSupported:", isSupported);
      console.log("Service Worker available:", "serviceWorker" in navigator);
      console.log("Current origin:", window.location.origin);

      if (!isSupported) {
        console.warn("Firebase messaging is not supported in this browser");
        return null;
      }

      if (!("serviceWorker" in navigator)) {
        console.warn("Service Worker is not supported in this browser");
        return null;
      }

      // Wait for service worker to be ready
      const registration = await navigator.serviceWorker.ready;
      console.log("Service Worker registered:", registration);

      // Give the service worker time to initialize Firebase
      // The service worker receives the config from index.js after registration
      console.log("[firebase.js] Waiting 2 seconds for service worker to initialize...");
      await new Promise((resolve) => setTimeout(resolve, 2000));
      console.log("[firebase.js] Wait complete, initializing messaging");

      // Now it's safe to get messaging
      const messaging = getMessaging(app);
      console.log("Firebase Messaging initialized successfully");
      messagingInstance = messaging;
      return messaging;
    } catch (error) {
      console.error("Error initializing Firebase Messaging:", error);
      messagingPromise = null; // Reset so we can retry
      throw error;
    }
  })();

  return messagingPromise;
}

export async function generateToken() {
  try {
    // Skip Firebase in WebViews
    if (isWebView()) {
      console.log("Running in WebView - skipping FCM token generation");
      return null;
    }

    // Ensure messaging is initialized
    const messaging = await initializeMessaging();

    if (!messaging) {
      console.warn("Firebase messaging is not available.");
      return null;
    }

    // Request notification permission
    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      throw new Error("Notification permission not granted.");
    }

    // Get device ID using FingerprintJS
    const fp = await FingerprintJS.load();
    const { visitorId: deviceId } = await fp.get();
    localStorage.setItem("device_id", deviceId);

    // Ensure service worker is ready
    if (!("serviceWorker" in navigator)) {
      throw new Error("Service workers are not supported in this browser.");
    }
    const registration = await navigator.serviceWorker.ready;

    // Generate FCM token
    const token = await getToken(messaging, {
      vapidKey: process.env.REACT_APP_FB_VAPID_KEY,
      serviceWorkerRegistration: registration,
    });

    if (token) {
      localStorage.setItem("fcm_token", token);
      return { token, device_id: deviceId };
    }

    throw new Error("No FCM registration token available.");
  } catch (error) {
    console.error("Error generating token:", error);
    return null;
  }
}
